import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers';
import { isValid } from 'date-fns';
import {
  tr, enUS, ru, kk, az,
} from 'date-fns/locale';
import GenericSelect from '../../../components/ui/GenericSelect';
import { todayIso } from '../clock';
import { periodOptions, isValidCustomRange } from '../format';
import { isIsoDay } from '../../../utils/attendanceFilterQuery';

const localeMap = {
  tr, en: enUS, ru, kk, az,
};

const MIN_DATE = new Date(2000, 0, 1);
const MAX_DATE = new Date(2100, 11, 31);

const CustomRange = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Pickers = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RangeError = styled.div`
  font-size: 11px;
  color: #B42318;
  max-width: 300px;
`;

const pad = (n) => String(n).padStart(2, '0');
const toIso = (date) => `${String(date.getFullYear()).padStart(4, '0')}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const fromIso = (iso) => {
  if (!isIsoDay(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * MUI X v8 klavye sözleşmesi (inceleme M7): alana yazılırken HER hane geçerli bir tarih üretir ve onChange+onAccept
 * tetiklenir — değer anında uygulanırsa her hane bir istek atar ve yarım yıl (0002) uyarısı yanıp söner. Değer yalnız
 * takvimden seçimde (onAccept, ctx.source === 'view') ya da Enter / alandan çıkışta, doğrulama hatası yoksa uygulanır.
 */
export function acceptedDate(iso, ctx) {
  if (!iso || ctx?.validationError) return null;
  return ctx?.source === 'view' ? iso : null;
}

// İnce (small) MUI tarih alanı — yüzen süzgeç çubuğuna sığsın diye paylaşılan tam boy DateTimePicker yerine.
function CompactDate({
  label, value, onCommit, lang, testId,
}) {
  const [draft, setDraft] = useState(value || null);
  const [invalid, setInvalid] = useState(null);
  useEffect(() => { setDraft(value || null); setInvalid(null); }, [value]);

  const commit = (iso, error) => {
    if (!iso || error) return;
    if (iso !== value) onCommit(iso);
  };

  return (
    <span
      data-compact-date={testId}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit(draft, invalid);
        }
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[lang] || tr}>
        <DatePicker
          label={label}
          value={fromIso(draft)}
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
          format={lang === 'en' ? 'MM/dd/yyyy' : 'dd.MM.yyyy'}
          onChange={(date, ctx) => {
            const iso = date && isValid(date) ? toIso(date) : null;
            setDraft(iso);
            setInvalid(iso ? (ctx?.validationError || null) : 'empty');
          }}
          onAccept={(date, ctx) => {
            const iso = acceptedDate(date && isValid(date) ? toIso(date) : null, ctx);
            if (iso) commit(iso, null);
          }}
          slotProps={{
            textField: {
              size: 'small',
              onBlur: () => commit(draft, invalid),
              sx: { width: 150, '& .MuiInputBase-input': { fontSize: 12 } },
            },
            popper: { style: { zIndex: 9999999 } },
          }}
        />
      </LocalizationProvider>
    </span>
  );
}

// Dönem: son 12 ay ya da Özel Aralık (en fazla 366 gün). Özel aralığa geçince alanlar o anki dönemle dolar (boş seçim
// istek göndermez, panel "tarih seçin" durumuna düşer). Geçersiz aralıkta istek atılmaz, alanın altında neden yazar.
export default function PeriodFilter({ filters, setFilters, query }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const options = useMemo(() => periodOptions(todayIso(), lang, t), [lang, t]);
  const custom = filters.periodKey === 'custom';

  let error = null;
  if (custom && filters.customFrom && filters.customTo && !isValidCustomRange(filters.customFrom, filters.customTo)) {
    error = filters.customFrom > filters.customTo
      ? t('attendanceDashboard.filters.invalidRange')
      : t('attendanceDashboard.filters.rangeTooLong');
  }

  const selectPeriod = (value) => setFilters((prev) => {
    const periodKey = value || prev.periodKey;
    if (periodKey === 'custom' && prev.periodKey !== 'custom') {
      return {
        ...prev, periodKey, customFrom: prev.customFrom || query?.from || null, customTo: prev.customTo || query?.to || null,
      };
    }
    return { ...prev, periodKey };
  });

  return (
    <>
      <GenericSelect
        options={options}
        value={filters.periodKey}
        clearable={false}
        onChange={selectPeriod}
        placeholder={t('attendanceDashboard.filters.period')}
        size="small"
        direction="top"
      />
      {custom && (
        <CustomRange data-wide data-custom-range>
          <Pickers>
            <CompactDate
              testId="from"
              label={t('attendanceDashboard.filters.from')}
              value={filters.customFrom}
              lang={lang}
              onCommit={(value) => setFilters((prev) => ({ ...prev, customFrom: value }))}
            />
            <CompactDate
              testId="to"
              label={t('attendanceDashboard.filters.to')}
              value={filters.customTo}
              lang={lang}
              onCommit={(value) => setFilters((prev) => ({ ...prev, customTo: value }))}
            />
          </Pickers>
          {error ? <RangeError role="alert" data-range-error>{error}</RangeError> : null}
        </CustomRange>
      )}
    </>
  );
}
